import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import Carousel from 'Common/components/ui/Carousel';
import { useViewport } from 'Common/hooks';
import { Product } from 'Common/types';
import { CatalogQuickView } from 'Common/components/catalog';
import * as GTM from 'Common/constants/gtm';
import { GTM as gtmUtil } from 'Common/utils';
import ScrollIndicator from '../ui/ScrollIndicator';

const CarouselProductCard = ({ item, index, visibleIndices, RecommendedProductCard, onOpenQuick, onSelectProduct, hasBorder }) => {
    const isVisible = visibleIndices?.includes(index);
    
    return (
        <RecommendedProductCard
            product={item}
            index={index}
            isActive={isVisible}
            onOpenQuick={onOpenQuick}
            onSelectProduct={onSelectProduct}
            hasBorder={hasBorder}
        />
    );
};

CarouselProductCard.propTypes = {
    item: Product,
    index: px.number,
    visibleIndices: px.arrayOf(px.number),
    RecommendedProductCard: px.elementType.isRequired,
    onOpenQuick: px.func.isRequired,
    onSelectProduct: px.func.isRequired,
    hasBorder: px.bool,
};

export default function ProductRecommendations({
    className,
    checkoutPageLink,
    isUserAuthenticated,
    ProductPartialView,
    products = [],
    RecommendedProductCard,
    trackProductQuickviewClick,
    itemStatusTooltip,
    totalQty = 0,
    useChildTable,
    useRelatedEntries = false
}) {
    const [productData, setProductData] = React.useState(null);
    const [showQuick, setShowQuick] = React.useState(false);
    const [showIndicator, setShowIndicator] = React.useState(false);
    const [visibleCount, setVisibleCount] = React.useState(1);
    const gtmImpression = React.useRef(false);
    const carouselId = React.useId();
    const viewport = useViewport();

    // Determine if mobile based on viewport
    const isMobile = React.useMemo(() => viewport.is.lt('md'), [viewport]);

    const setQuick = React.useCallback(
        (product) => () => {
            setProductData({ Product: product });
            trackProductQuickviewClick(product.Code);
            setShowQuick(true);
        },
        [trackProductQuickviewClick]
    );

    const hideQuickView = React.useCallback(() => setShowQuick(false), []);

    const onSelectProduct = React.useCallback(
        (product, index, onClick) => () => {
            gtmUtil.updateDataLayer(gtmUtil.mapEntityToImpClick(product, GTM.TAGS.RECOMMENDED_QUICK));
            onClick();
        },
        []
    );

    const handleCarouselScroll = React.useCallback((firstIndex, visCount) => {
        setVisibleCount(visCount);
    }, []);

    // Check if scrolling is needed
    React.useEffect(() => {
        const checkScrollNeeded = () => {
            const container = document.querySelector(`#${carouselId} .Carousel__container`);

            if (container) {
                const hasScroll = container.scrollWidth > container.clientWidth;
                
                setShowIndicator(hasScroll && visibleCount === 1);
            }
        };

        checkScrollNeeded();
        window.addEventListener('resize', checkScrollNeeded);

        const timer = setTimeout(checkScrollNeeded, 100);

        return () => {
            window.removeEventListener('resize', checkScrollNeeded);
            clearTimeout(timer);
        };
    }, [carouselId, products.length, visibleCount]);

    React.useEffect(() => {
        if (!products?.length) return;
        if (!gtmImpression.current) {
            gtmUtil.updateDataLayer(gtmUtil.mapEntityToImpressions(products, GTM.TAGS.RECOMMENDED_QUICK));
            gtmImpression.current = true;
        }
        window.yotpoWidgetsContainer?.initWidgets?.();
    }, [products]);

    // Use isMobile to determine carousel sizing
    // Mobile: show 1 item at a time
    // Desktop: show fixed-width cards
    const carouselItemsPerView = isMobile ? 1 : null;
    const carouselItemWidth = isMobile ? null : 350;

    if (!products?.length) {
        return null;
    }

    return (
        <div className={cx('ProductRecommendations', className)}>
            <Carousel
                animateScroll={true}
                BtnScrollLeft={<i className="fas fa-chevron-left" aria-hidden="true" />}
                BtnScrollRight={<i className="fas fa-chevron-right" aria-hidden="true" />}
                id={carouselId}
                Item={CarouselProductCard}
                itemProps={{
                    RecommendedProductCard,
                    onOpenQuick: setQuick,
                    onSelectProduct,
                    hasBorder: useRelatedEntries
                }}
                items={products}
                keyBy={(product) => product.Code}
                itemsPerView={carouselItemsPerView}
                itemWidth={carouselItemWidth}
                gap={20}
                onScroll={handleCarouselScroll}
            />
            
            {showIndicator && (
                <ScrollIndicator
                    showDots={true}
                    itemClass=".CarouselButton"
                    targetSelector={`#${carouselId} .Carousel__container`}
                />
            )}
            
            {showQuick && (
                <CatalogQuickView
                    Product={ProductPartialView}
                    productProps={{
                        productData: productData,
                        checkoutPageLink: checkoutPageLink,
                        gtmListValue: GTM.TAGS.RECOMMENDED_QUICK,
                        totalQty: totalQty,
                        useChildTable: useChildTable
                    }}
                    isUserAuthenticated={isUserAuthenticated}
                    onClose={hideQuickView}
                    itemStatusTooltip={itemStatusTooltip}
                />
            )}
        </div>
    );
}

ProductRecommendations.propTypes = {
    className: px.string,
    checkoutPageLink: px.string,
    isUserAuthenticated: px.oneOfType([px.string, px.bool]),
    ProductPartialView: px.elementType,
    products: px.arrayOf(Product),
    RecommendedProductCard: px.elementType,
    trackProductQuickviewClick: px.func,
    itemStatusTooltip: px.string,
    totalQty: px.number,
    useChildTable: px.bool,
    useRelatedEntries: px.bool
};